/**
 * component-cart-drawer.js — Cart Drawer Web Component for Mirai Online
 *
 * CartDrawer custom element:
 *  - Opens/closes the slide-out cart drawer panel
 *  - Listens for 'cart:open' and 'cart:update' custom events on document
 *  - Updates drawer content via Section Rendering API (Shopify /cart/change.js sections param)
 *  - Implements ARIA dialog pattern: role="dialog", aria-modal, focus trap, Escape key, inert background
 *  - Updates cart count badge ([data-cart-count]) after every cart operation
 *
 * Security notes (per threat model T-01-14 through T-01-18):
 *  - Section Rendering HTML comes from Shopify's server (Liquid-rendered, not user input) — safe to inject
 *  - No client-side price logic; all prices come from server response
 *  - Quantity buttons are disabled during fetch to prevent request flooding (T-01-16)
 *  - Never constructs HTML strings from user-provided data
 */

/* ============================================================
   CART DRAWER WEB COMPONENT
   ============================================================ */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Stable bound references for addEventListener/removeEventListener symmetry
    this._boundOpen = this.open.bind(this);
    this._boundKeydown = this.handleKeydown.bind(this);

    // State
    this._releaseFocusTrap = null;
    this._triggerElement = null;
  }

  connectedCallback() {
    // Listen for global 'cart:open' event (dispatched by header cart icon + add-to-cart flows)
    document.addEventListener('cart:open', this._boundOpen);

    // Listen for 'cart:update' event (dispatched after cart operations complete)
    document.addEventListener('cart:update', this._handleCartUpdate.bind(this));

    // Listen for global keydown (Escape to close)
    document.addEventListener('keydown', this._boundKeydown);

    // Cache DOM references (elements outside data-cart-drawer-content — they persist across refreshes)
    this._cacheStaticRefs();

    // Wire overlay and close button
    this._overlay?.addEventListener('click', () => this.close());
    this._closeBtn?.addEventListener('click', () => this.close());

    // Delegate quantity/remove button clicks on content wrapper
    // Uses event delegation — re-queries after content refresh automatically
    this.addEventListener('click', this._handleContentClick.bind(this));

    // Theme editor section reload compatibility
    this.addEventListener('section:load', () => {
      this._cacheStaticRefs();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('cart:open', this._boundOpen);
    document.removeEventListener('keydown', this._boundKeydown);
  }

  /**
   * Cache references to elements that persist across Section Rendering refreshes.
   * The overlay and close button are outside [data-cart-drawer-content] — they survive content swaps.
   */
  _cacheStaticRefs() {
    this._overlay = this.previousElementSibling?.matches('[data-cart-overlay]')
      ? this.previousElementSibling
      : document.querySelector('[data-cart-overlay]');
    this._closeBtn = this.querySelector('[data-cart-close]');
    this._content = this.querySelector('[data-cart-drawer-content]');
  }

  /**
   * Returns true if the drawer is currently open (aria-hidden="false").
   */
  get isOpen() {
    return this.getAttribute('aria-hidden') === 'false';
  }

  /**
   * Open the cart drawer.
   * @param {CustomEvent} [event] - Optional event with detail.triggerElement
   */
  open(event) {
    // Store the element that triggered open so we can return focus on close
    if (event?.detail?.triggerElement) {
      this._triggerElement = event.detail.triggerElement;
    } else if (!this.isOpen) {
      // Capture active element before drawer takes focus
      this._triggerElement = document.activeElement;
    }

    // Show the drawer
    this.setAttribute('aria-hidden', 'false');

    // Show the overlay
    if (this._overlay) {
      this._overlay.setAttribute('aria-hidden', 'false');
      this._overlay.classList.add('is-visible');
    }

    // Prevent background scroll
    window.Theme.lockScroll();

    // Apply inert to background content (accessibility — prevents Tab escape)
    document.querySelector('#MainContent')?.setAttribute('inert', '');
    document.querySelector('#header-group')?.setAttribute('inert', '');

    // Activate focus trap — re-queries focusable elements on every Tab (per Pitfall 5)
    this._releaseFocusTrap = this.trapFocus();

    // Focus the close button first
    this.querySelector('[data-cart-close]')?.focus();

    // Dispatch open event so other components can react
    document.dispatchEvent(new CustomEvent('cart:opened', { bubbles: false }));
  }

  /**
   * Close the cart drawer.
   */
  close() {
    this.setAttribute('aria-hidden', 'true');

    // Hide overlay
    if (this._overlay) {
      this._overlay.setAttribute('aria-hidden', 'true');
      this._overlay.classList.remove('is-visible');
    }

    // Restore background scroll
    window.Theme.unlockScroll();

    // Remove inert from background
    document.querySelector('#MainContent')?.removeAttribute('inert');
    document.querySelector('#header-group')?.removeAttribute('inert');

    // Release focus trap
    if (this._releaseFocusTrap) {
      this._releaseFocusTrap();
      this._releaseFocusTrap = null;
    }

    // Return focus to the element that triggered the drawer open
    this._triggerElement?.focus();
    this._triggerElement = null;

    // Dispatch close event
    document.dispatchEvent(new CustomEvent('cart:close', { bubbles: false }));
  }

  /**
   * Handle global keydown events.
   * Closes drawer on Escape key.
   */
  handleKeydown(e) {
    if (e.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  /**
   * Focus trap implementation.
   * Re-queries focusable elements on EVERY Tab keypress to handle dynamic content updates
   * (per Pitfall 5 from RESEARCH.md — cached lists go stale after Section Rendering refresh).
   *
   * @returns {Function} Cleanup function to release the trap
   */
  trapFocus() {
    const focusableSelectors = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    // Re-query on every Tab press (not cached at open time)
    const getFocusable = () => Array.from(this.querySelectorAll(focusableSelectors));

    const handleKeydown = (e) => {
      if (e.key !== 'Tab') return;

      const focusable = getFocusable();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: wrap backward from first to last
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab: wrap forward from last to first
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    this.addEventListener('keydown', handleKeydown);

    return () => {
      this.removeEventListener('keydown', handleKeydown);
    };
  }

  /**
   * Event delegation handler for clicks inside the cart drawer content.
   * Handles quantity +/- buttons and remove buttons.
   */
  _handleContentClick(e) {
    // Quantity buttons have both data-line and data-quantity
    const qtyBtn = e.target.closest('[data-line][data-quantity]');
    if (!qtyBtn) return;

    const line = parseInt(qtyBtn.dataset.line, 10);
    const quantity = parseInt(qtyBtn.dataset.quantity, 10);

    if (!isNaN(line) && !isNaN(quantity)) {
      this.updateQuantity(line, quantity);
    }
  }

  /**
   * Handle 'cart:update' event dispatched by external add-to-cart flows.
   * @param {CustomEvent} event - detail.sections['cart-drawer'] contains fresh HTML
   */
  _handleCartUpdate(event) {
    // Ignore the event we dispatched ourselves — updateQuantity() has already
    // called refresh() with this exact HTML. Re-refreshing here re-rendered the
    // line items a second time and fired another cart.js, doubling the work and
    // the focus loss on every single quantity click.
    if (this._selfDispatch) return;

    const sections = event?.detail?.sections;
    if (sections && sections['cart-drawer']) {
      this.refresh(sections['cart-drawer']);
    }
  }

  /**
   * Update a cart line item quantity.
   * POSTs to /cart/change.js with bundled Section Rendering request.
   * Disables quantity buttons during fetch to prevent flooding (T-01-16).
   *
   * @param {number} line - 1-based line item index
   * @param {number} quantity - New quantity (0 = remove)
   */
  async updateQuantity(line, quantity) {
    if (this._updating) return;
    this._updating = true;

    this._clearError();

    // Remember what had focus so it can be restored after the content swap.
    const activeEl = document.activeElement;
    const focusKey = activeEl && activeEl.closest && activeEl.closest('[data-line]')
      ? activeEl.getAttribute('data-cart-action') + ':' + activeEl.getAttribute('data-line')
      : null;

    // Disable all quantity/remove buttons during fetch
    const buttons = this.querySelectorAll('[data-line]');
    buttons.forEach(btn => {
      btn.disabled = true;
      btn.classList.add('cart-drawer--loading');
    });

    let swapped = false;

    try {
      const response = await fetch(window.Theme.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity,
          sections: 'cart-drawer'
        })
      });

      // Read the body BEFORE deciding. Shopify returns 422 with a human-readable
      // `description` ("You can only add 2 of that item…") — the old code threw
      // on !response.ok without reading it and only console.warn'd, so an
      // over-inventory "+" silently did nothing at all with no message shown.
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        this._showError((data && data.description) || this._errorStrings().generic);
        return;
      }

      // Update drawer content via Section Rendering API response (Pitfall 3: null-check)
      if (data && data.sections && data.sections['cart-drawer']) {
        this.refresh(data.sections['cart-drawer'], { restoreFocusKey: focusKey });
        swapped = true;
      }

      // Dispatch update event for other components to react.
      //
      // NOTE: this drawer's OWN cart:update listener must not re-refresh from
      // this — refresh() already ran above. It used to, which meant every "+"
      // click fired 1 change.js + 3 cart.js and re-rendered the line items twice.
      this._selfDispatch = true;
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: false,
        detail: { cart: data, sections: data.sections }
      }));
      this._selfDispatch = false;

    } catch (err) {
      this._showError(this._errorStrings().generic);
    } finally {
      this._updating = false;

      // Only re-enable if refresh() did NOT run. When it did, the server-rendered
      // markup is authoritative — it correctly ships `disabled` on the "−" button
      // of a qty-1 line, and blanket re-enabling stripped that attribute back off,
      // turning a one-click accidental removal into a live hazard.
      if (!swapped) {
        this.querySelectorAll('[data-line]').forEach(btn => {
          btn.disabled = false;
          btn.classList.remove('cart-drawer--loading');
        });
      }
    }
  }

  _errorStrings() {
    return {
      generic:
        (window.Theme && window.Theme.strings && window.Theme.strings.cart_error) ||
        'Could not update your cart. Please try again.'
    };
  }

  _showError(message) {
    const el = this.querySelector('[data-cart-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  _clearError() {
    const el = this.querySelector('[data-cart-error]');
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  /**
   * Refresh drawer content from Section Rendering API HTML.
   * Null-checks before DOM replacement (Pitfall 3 from RESEARCH.md).
   * HTML comes from Shopify's server-rendered Liquid — safe to inject (T-01-15).
   *
   * @param {string} sectionsHtml - Server-rendered HTML string for cart-drawer section
   */
  refresh(sectionsHtml, options = {}) {
    // Pitfall 3: null-check before proceeding
    if (!sectionsHtml) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionsHtml, 'text/html');
    const freshContent = doc.querySelector('[data-cart-drawer-content]');

    if (!freshContent) return;

    // Update the content ref before swapping (needed below for re-caching)
    const contentWrapper = this.querySelector('[data-cart-drawer-content]');
    if (contentWrapper) {
      // Replace inner HTML with fresh server-rendered content (safe — Shopify Liquid output)
      contentWrapper.innerHTML = freshContent.innerHTML;
    }

    // Re-cache close button reference (it's inside data-cart-drawer-content)
    this._closeBtn = this.querySelector('[data-cart-close]');

    // Re-wire close button after content swap
    this._closeBtn?.addEventListener('click', () => this.close());

    // innerHTML replacement destroys the focused node, dropping focus to <body>.
    // The drawer's focus trap listens on the drawer element, so with focus on
    // <body> it stopped firing entirely and the next Tab walked out of the
    // "trapped" drawer into the footer behind the scrim. Put focus back on the
    // equivalent control in the new markup.
    const key = options.restoreFocusKey;
    if (key) {
      const [action, line] = key.split(':');
      const target =
        this.querySelector(`[data-cart-action="${action}"][data-line="${line}"]:not([disabled])`) ||
        this.querySelector(`[data-line="${line}"]:not([disabled])`) ||
        this._closeBtn;
      if (target && typeof target.focus === 'function') target.focus();
    }

    // Update badge count
    this.updateCartBadge();
  }

  /**
   * Fetch current cart state and update all [data-cart-count] badge elements.
   * Hides badge when cart is empty.
   */
  updateCartBadge() {
    const cartUrl = (window.Theme?.routes?.root || '/') + 'cart.js';
    fetch(cartUrl)
      .then(r => {
        if (!r.ok) throw new Error(`Cart fetch failed: ${r.status}`);
        return r.json();
      })
      .then(cart => {
        document.querySelectorAll('[data-cart-count]').forEach(badge => {
          badge.textContent = cart.item_count;
          badge.hidden = cart.item_count === 0;
        });
      })
      .catch(err => {
        console.warn('[CartDrawer] updateCartBadge failed:', err);
      });
  }
}

// Registration guard: only define once even if section is rendered multiple times
if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

/* ============================================================
   ADD-TO-CART HELPER
   Exported pattern for product forms in future phases.
   After POSTing /cart/add.js with sections: 'cart-drawer',
   call this to open + refresh the drawer in one flow (per D-06).
   ============================================================ */

/**
 * Opens the cart drawer and refreshes its content with fresh section HTML.
 * Called by product forms after a successful add-to-cart.
 *
 * @param {Object} options
 * @param {Element} [options.triggerElement] - Element that triggered add-to-cart (for focus return)
 * @param {Object} [options.sections] - sections object from /cart/add.js response
 */
function openCartDrawerWithContent({ triggerElement = null, sections = null } = {}) {
  const drawerEl = document.querySelector('cart-drawer');
  if (!drawerEl) return;

  // Refresh content first if we have fresh section HTML
  if (sections && sections['cart-drawer']) {
    drawerEl.refresh(sections['cart-drawer']);
  }

  // Dispatch the open event with trigger element context
  document.dispatchEvent(new CustomEvent('cart:open', {
    bubbles: false,
    detail: { triggerElement }
  }));
}

// Expose on window for use by product form scripts in later phases
window.CartDrawerHelpers = window.CartDrawerHelpers || {};
window.CartDrawerHelpers.openCartDrawerWithContent = openCartDrawerWithContent;
